/**
 * Dynamic Lucide Icon Component
 *
 * Loads Lucide icons dynamically based on icon name string.
 * Useful when icon names come from backend API.
 *
 * @example
 * <DynamicLucideIcon name="Target" size={24} />
 * <DynamicLucideIcon name="Users" className="text-primary" />
 */

import React, { useMemo } from "react";
import * as LucideIcons from "lucide-react";
import { LucideProps } from "lucide-react";
import { logger } from "@/utils/logger"
interface DynamicLucideIconProps extends Omit<LucideProps, "ref"> {
  /**
   * Name of the Lucide icon in PascalCase (e.g., "Target", "ChartPie", "Users")
   */
  name: string;
  /**
   * Fallback icon name if the specified icon is not found
   * @default "Target"
   */
  fallback?: string;
}

/**
 * Dynamically renders a Lucide icon by name
 * Falls back to Target icon if the specified icon doesn't exist
 */
export const DynamicLucideIcon: React.FC<DynamicLucideIconProps> = React.memo(
  ({ name, fallback = "Target", ...props }) => {
    const IconComponent = useMemo(() => {
      // Try to get the requested icon
      const RequestedIcon = (LucideIcons as any)[name];

      if (RequestedIcon) {
        return RequestedIcon;
      }

      // If not found, try fallback
      const FallbackIcon = (LucideIcons as any)[fallback];

      if (FallbackIcon) {
        logger.warn(
          `[DynamicLucideIcon] Icon "${name}" not found in lucide-react. Using fallback "${fallback}".`
        );
        return FallbackIcon;
      }

      // Last resort: use Target icon
      logger.warn(
        `[DynamicLucideIcon] Neither "${name}" nor fallback "${fallback}" found. Using Target icon.`
      );
      return LucideIcons.Target;
    }, [name, fallback]);

    return <IconComponent {...props} />;
  }
);

DynamicLucideIcon.displayName = "DynamicLucideIcon";
